import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatPrice(
  amount: number | null,
  currency = 'EUR',
): string {
  if (amount == null) return 'Price on request'
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso))
}

const COUNTRY_NAME_CACHE = new Map<string, Intl.DisplayNames>()

/**
 * Localized country name for a 2-letter ISO code (e.g. "NL" → "Nederland" in Dutch).
 * Falls back to the input value when it is not a valid ISO code.
 */
export function formatCountry(value: string | null | undefined, locale: 'nl' | 'en' = 'nl'): string {
  if (!value) return ''
  const trimmed = value.trim()
  if (trimmed.length !== 2) return trimmed
  let display = COUNTRY_NAME_CACHE.get(locale)
  if (!display) {
    display = new Intl.DisplayNames([locale === 'en' ? 'en' : 'nl-NL'], { type: 'region' })
    COUNTRY_NAME_CACHE.set(locale, display)
  }
  return display.of(trimmed.toUpperCase()) ?? trimmed
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function truncate(text: string, max = 120): string {
  if (text.length <= max) return text
  return text.slice(0, max).trimEnd() + '\u2026'
}

/**
 * Strip the "Material number: {MPN} Description: " prefix that B&R catalog
 * ingestion stores verbatim in short_description and long_description fields.
 * Returns the descriptive text only, or the original string if no prefix found.
 */
export function cleanBrDescription(raw: string | null | undefined): string {
  if (!raw) return ''
  const match = raw.match(/^Material number:\s*\S+\s+Description:\s*([\s\S]+)/)
  return match ? match[1].trim() : raw
}

/**
 * Extract a short, headline-worthy title from a B&R description blob. Many
 * B&R short_descriptions are full multi-clause specifications; rendering the
 * full text as an H1 produces a giant wrapped heading. Take the first natural
 * phrase (split on `,` `;` or ". ") and cap at `maxLen` chars. Returns the
 * input as-is when it's already short enough.
 */
export function extractTitleFromDescription(desc: string | null | undefined, maxLen = 80): string {
  if (!desc) return ''
  const cleaned = desc.trim()
  if (cleaned.length <= maxLen) return cleaned
  const firstPhrase = cleaned.split(/[,;]|\.\s+/)[0].trim()
  if (firstPhrase.length > 0 && firstPhrase.length <= maxLen) return firstPhrase
  return cleaned.slice(0, maxLen).trimEnd() + '…'
}

/**
 * Parse a B&R catalog description string into bullet points.
 *
 * B&R stores feature lists as concatenated text without delimiters:
 * "Tool-free wiring using push-in technologySimple wire release using a screwdriver"
 * Split on a lowercase→uppercase boundary (no whitespace between words).
 * Returns an array of non-empty strings, or an empty array when the text
 * cannot be meaningfully split (fewer than 2 items after filtering).
 */
export function parseDescriptionBullets(text: string): string[] {
  if (!text) return []
  // Insert a sentinel between a lowercase letter and an uppercase letter that
  // is immediately adjacent (no space). The \x00 is used as split marker.
  const marked = text.replace(/([a-z])([A-Z])/g, '$1\x00$2')
  const parts = marked.split('\x00').map(s => s.trim()).filter(s => s.length >= 4)
  return parts.length >= 2 ? parts : []
}
